import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, CheckCircle, Home as HomeIcon, TrendingUp, Shield, Users } from 'lucide-react';
import { floorPlans, testimonials, buildProcess, companyInfo } from '../data/mockData';

const Home = () => {
  const featuredPlans = floorPlans.slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="EC Home Builds | Custom Home Builder Columbus Ohio"
        description="Build your dream custom home on your own lot in Columbus, Ohio. 11+ years experience, fixed-price contracts, quality craftsmanship. Schedule a consultation today!"
        keywords="custom home builder Columbus Ohio, build on your lot Columbus, custom homes Ohio, new home construction Columbus, duplex builder Ohio"
        path="/"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white text-center flex flex-col justify-center h-full py-8">
          {/* Logo */}
          <div className="mb-4">
            <img
              src={companyInfo.logoUrl}
              alt="EC Home Builds"
              className="w-full max-w-3xl mx-auto h-auto"
            />
          </div>
          
          {/* Hero Content */}
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight tracking-tight">
              Build Your Dream Home On Your Own Lot
            </h1>
            <p className="text-sm md:text-base mb-5 text-gray-200 leading-relaxed max-w-2xl mx-auto">
              Custom homes built with craftsmanship, transparency, and modern design in Central Ohio.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link to="/contact">
                <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white text-base px-8 py-5 shadow-2xl hover:shadow-[#DAA520]/50 transition-all duration-300 hover:scale-105 font-semibold">
                  Schedule Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/floor-plans">
                <Button variant="outline" className="bg-white/10 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-[#262626] text-base px-8 py-5 transition-all duration-300 hover:scale-105 font-semibold">
                  Browse Floor Plans
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About EC Home Builds */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-6">
                About EC Home Builds
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Founded and led by <span className="font-semibold text-[#DAA520]">Erick Cordero</span>, EC Home Builds is dedicated to bringing custom home solutions. Built on Erick's extensive experience, our reputation is founded on three key pillars: exceptional craftsmanship, transparent project management, and unwavering commitment to our clients.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                With over 11 years of hands-on experience in construction and real estate development, we understand that building a home is much more than just laying foundations and framing walls. It's about creating lasting value and building equity for both families and investors.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#262626] mb-1">11+ Years Experience</h3>
                    <p className="text-sm text-gray-600">Proven expertise in custom home building</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#262626] mb-1">Fixed-Price Contracts</h3>
                    <p className="text-sm text-gray-600">No surprises, complete transparency</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1577561776790-1a20c419b1a2?q=85&w=2000"
                alt="Professional Construction Planning"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Build on Your Lot Model */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Our Model: Build on Your Lot
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You own the land, we handle everything else
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: HomeIcon,
                title: 'You Own the Land',
                description: 'Bring your lot, and we handle everything else – from initial design concepts to final construction.'
              },
              {
                icon: Users,
                title: 'Flexible Design Options',
                description: 'Select from proven EC Home Builds plans or collaborate with us to customize your ideal home.'
              },
              {
                icon: Shield,
                title: 'Fixed-Price Contracts',
                description: 'Full transparency from day one – no surprises, no hidden costs, just honest partnership.'
              },
              {
                icon: CheckCircle,
                title: 'Turnkey Delivery',
                description: 'From groundbreaking through permitting to keys in hand, we manage every detail of your build.'
              }
            ].map((item, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#DAA520]/10 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="text-[#DAA520]" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#262626] mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Floor Plans */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Featured Home Plans
            </h2>
            <p className="text-xl text-gray-600">
              Discover designs crafted for your lifestyle
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredPlans.map((plan) => (
              <Card key={plan.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="relative overflow-hidden h-64">
                  <img
                    src={plan.thumbnail}
                    alt={plan.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-[#DAA520] text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {plan.sqft} sq. ft.
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#262626] mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.style}</p>
                  <div className="flex items-center justify-between text-sm text-gray-700 mb-4">
                    <span>{plan.bedrooms} Bed</span>
                    <span>•</span>
                    <span>{plan.bathrooms} Bath</span>
                    <span>•</span>
                    <span>{plan.garage}</span>
                  </div>
                  <Link to={`/floor-plans/${plan.id}`}>
                    <Button className="w-full bg-[#262626] hover:bg-[#DAA520] text-white transition-colors duration-300">
                      View Details
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/floor-plans">
              <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg">
                View All Floor Plans
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Construction Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              The EC Home Builds Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven six-phase approach ensures your build stays on track, on budget, and aligned with your vision
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {buildProcess.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-white border-2 border-gray-100 rounded-lg p-6 hover:border-[#DAA520] transition-all duration-300 hover:shadow-xl">
                  <div className="w-12 h-12 bg-[#DAA520] text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-[#262626] mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/build-on-your-lot">
              <Button variant="outline" className="border-2 border-[#DAA520] text-[#DAA520] hover:bg-[#DAA520] hover:text-white px-8 py-6 text-lg transition-all duration-300">
                Learn More About Our Process
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Investment Advantage */}
      <section className="py-20 bg-[#262626] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              The Investment Advantage
            </h2>
            <p className="text-xl text-gray-300">
              Own the Land. Own the Build. Keep the Equity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: TrendingUp,
                value: '10-20%',
                label: 'Equity Gain',
                description: 'Clients typically gain 10–20% equity upon completion, building wealth from day one.'
              },
              {
                icon: Users,
                value: '3',
                label: 'Ideal Client Types',
                description: 'Perfect for owner-occupants seeking their dream home, savvy investors, or experienced developers.'
              },
              {
                icon: Shield,
                value: '100%',
                label: 'Local Expertise',
                description: 'Complete turnkey process backed by deep Central Ohio market knowledge and relationships.'
              }
            ].map((item, index) => (
              <Card key={index} className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#DAA520] rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon size={32} />
                  </div>
                  <div className="text-5xl font-bold text-[#DAA520] mb-2">{item.value}</div>
                  <h3 className="text-xl font-bold mb-3">{item.label}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <p className="text-lg text-gray-300 max-w-4xl mx-auto mb-8">
              Whether you're building your forever home or expanding your investment portfolio, our model provides a clear path to wealth creation through real estate. Start with one custom home and scale to a full portfolio – we're equipped to support your growth every step of the way.
            </p>
            <Link to="/investor-builds">
              <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg">
                Explore Investment Opportunities
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300">
                <CardContent className="p-8">
                  <div className="text-5xl text-[#DAA520] mb-4">"</div>
                  <p className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                    {testimonial.text}
                  </p>
                  <p className="font-semibold text-[#262626]">– {testimonial.author}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Custom Build?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Let's transform your vision into reality. Schedule a consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
                Schedule Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/cost-estimate">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white text-white hover:bg-white hover:text-[#262626] px-8 py-6 text-lg">
                Get Cost Estimate
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
